import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Inject, OnDestroy, OnInit, signal, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSliderModule } from '@angular/material/slider';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';

import { ProfilePhotosService } from '../../../service/profilePhotos.service';
import { WebsocketService } from '../../../service/websocket.service';
import { User, UserLight } from '../../../model/user.model';

export type LivePhotoRecaptureMode = 'teacher' | 'student';

export interface LivePhotoRecaptureDialogData {
    mode: LivePhotoRecaptureMode;
    /** Élément <video> distant exposé par VideoStreamComponent (mode teacher uniquement). */
    remoteVideoElement?: HTMLVideoElement | null;
    /** Élément <video> local de l'élève (mode student uniquement, pour aperçu et cadrage). */
    localVideoElement?: HTMLVideoElement | null;
    /** Élève dont la photo d'identité va être remplacée. */
    user: User | UserLight;
    /** Identifiant de la session d'examen, pour les messages WebSocket. */
    examinationSessionId: number;
    /** Persona courante (utilisée par WebsocketService.prepare_message_data). */
    me?: any;
    /** Nom affiché dans le titre du dialog (mode student uniquement). */
    studentDisplayName?: string;
}

export interface LivePhotoRecaptureResult {
    status: 'validated' | 'cancelled';
    profilePhotoId?: number;
    photoUrl?: string;
}

type ViewMode = 'live' | 'review' | 'uploading';

/**
 * Modal de re-capture de la photo d'identité en direct.
 *
 * - Côté formateur (`mode: 'teacher'`) : affiche le flux vidéo distant de l'élève
 *   cloné depuis `VideoStreamComponent`, permet de capturer une frame, recadrer
 *   (zoom + rotation), uploader via `ProfilePhotosService.postProfilePhoto`
 *   (status `validated`) puis diffuse `photo_validated` via WebSocket.
 * - Côté élève (`mode: 'student'`) : informatif uniquement. La fermeture est
 *   pilotée par le parent via `ExaminationRoomComponent`.
 *
 * Spec : `examination/src/documentation/specs/live_photo_recapture.md`.
 */
@Component({
    selector: 'app-live-photo-recapture-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ImageCropperComponent,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSliderModule,
        TranslateModule,
    ],
    templateUrl: './live-photo-recapture-modal.component.html',
    styleUrls: ['./live-photo-recapture-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LivePhotoRecaptureModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('liveVideo') liveVideoRef: ElementRef<HTMLVideoElement>;

    readonly viewMode = signal<ViewMode>('live');
    readonly zoom = signal<number>(1);
    readonly uploadError = signal<string | null>(null);
    readonly capturing = signal<boolean>(false);
    /** Fichier remis au cropper ngx-image-cropper (image brute issue de la capture). */
    readonly capturedFile = signal<File | null>(null);
    /** Aperçu rond alimenté par les événements `imageCropped` du cropper. */
    readonly croppedPreviewUrl = signal<SafeUrl | null>(null);
    readonly editorTransform = computed(
        () => `scale(${this.zoom()})`,
    );
    /** Élève : data-url "figée" envoyée par le formateur dès qu'il capture la photo. */
    readonly frozenPhotoUrl = signal<string | null>(null);
    /** Élève : URL de la photo finale validée (servie par l'API). */
    readonly finalPhotoUrl = signal<string | null>(null);

    /** Transform appliquée à <image-cropper> (zoom + flip après rotation). */
    transform: ImageTransform = { translateUnit: 'px', scale: 1 };
    /** Rotation 90° du canvas cropper (cumulé). */
    canvasRotation = 0;

    /** Dernier Blob émis par le cropper, réutilisé au moment du validate(). */
    private lastCroppedBlob: Blob | null = null;
    /** Stream cloné depuis l'élément vidéo distant, à arrêter à la fermeture. */
    private clonedStream: MediaStream | null = null;

    constructor(
        public dialogRef: MatDialogRef<LivePhotoRecaptureModalComponent, LivePhotoRecaptureResult>,
        @Inject(MAT_DIALOG_DATA) public data: LivePhotoRecaptureDialogData,
        private profilePhotosService: ProfilePhotosService,
        private websocketService: WebsocketService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
    ) {
        // Empêcher la fermeture par échap / backdrop : utiliser les boutons explicites.
        this.dialogRef.disableClose = true;
    }

    ngOnInit(): void {
        if (this.data.mode === 'student') {
            this.viewMode.set('live');
        }
    }

    ngAfterViewInit(): void {
        if (this.data.mode === 'teacher') {
            this.attachRemoteStream();
        } else if (this.data.mode === 'student') {
            this.attachLocalStream();
        }
    }

    ngOnDestroy(): void {
        this.stopClonedStream();
    }

    // -------------------------------------------------------------------------
    // Mode teacher : attache le flux distant à <video #liveVideo>
    // -------------------------------------------------------------------------
    private attachRemoteStream(): void {
        const remote = this.data.remoteVideoElement;
        const liveVideo = this.liveVideoRef?.nativeElement;
        if (!remote || !liveVideo) {
            this.uploadError.set('examination.recapture.teacher.no-stream');
            return;
        }
        const srcObject = (remote as any).srcObject as MediaStream | null;
        if (srcObject && typeof srcObject.getVideoTracks === 'function') {
            // /!\ MediaStreamTrack.clone() est OBLIGATOIRE ici.
            // `new MediaStream(srcObject.getVideoTracks())` ne crée PAS une copie : il
            // référence les mêmes tracks. Au ngOnDestroy on appelle track.stop() pour
            // libérer notre stream local, ce qui couperait alors le flux ACS d'origine
            // (écran noir/figé pour le formateur et l'élève). clone() donne une track
            // indépendante : on peut la stopper sans impact sur le rendu d'ACS.
            const clonedTracks = srcObject.getVideoTracks().map(t => t.clone());
            this.clonedStream = new MediaStream(clonedTracks);
            liveVideo.srcObject = this.clonedStream;
            liveVideo.muted = true;
            liveVideo.play().catch(() => {
                this.uploadError.set('examination.recapture.teacher.no-stream');
            });
        } else {
            this.uploadError.set('examination.recapture.teacher.no-stream');
        }
    }

    /**
     * Mode student : clone le flux local (caméra de l'élève) dans <video #liveVideo>
     * afin que l'élève voie comment il apparaît au formateur et puisse se recadrer.
     */
    private attachLocalStream(): void {
        const local = this.data.localVideoElement;
        const liveVideo = this.liveVideoRef?.nativeElement;
        if (!local || !liveVideo) {
            return;
        }
        const srcObject = (local as any).srcObject as MediaStream | null;
        if (srcObject && typeof srcObject.getVideoTracks === 'function') {
            // Idem mode teacher : track.clone() obligatoire pour ne pas couper
            // le flux ACS local de l'élève lorsqu'on ferme la modal.
            const clonedTracks = srcObject.getVideoTracks().map(t => t.clone());
            this.clonedStream = new MediaStream(clonedTracks);
            liveVideo.srcObject = this.clonedStream;
            liveVideo.muted = true;
            liveVideo.play().catch(() => { /* noop */ });
        }
    }

    private stopClonedStream(): void {
        const liveVideo = this.liveVideoRef?.nativeElement;
        if (liveVideo) {
            try {
                liveVideo.pause();
            } catch (_) { /* noop */ }
            // Détacher AVANT stop() pour éviter que le moteur vidéo garde une référence.
            liveVideo.srcObject = null;
        }
        if (this.clonedStream) {
            this.clonedStream.getTracks().forEach(track => track.stop());
            this.clonedStream = null;
        }
    }

    // -------------------------------------------------------------------------
    // Mode teacher : capture / review / upload
    // -------------------------------------------------------------------------
    capture(): void {
        const liveVideo = this.liveVideoRef?.nativeElement;
        if (!liveVideo || liveVideo.readyState < 2) {
            return;
        }
        this.capturing.set(true);
        const canvas = document.createElement('canvas');
        const w = liveVideo.videoWidth || 640;
        const h = liveVideo.videoHeight || 480;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            this.capturing.set(false);
            this.uploadError.set('examination.recapture.teacher.no-stream');
            return;
        }
        try {
            ctx.drawImage(liveVideo, 0, 0, w, h);
            // Source du cropper : un File construit à partir du blob de la frame.
            canvas.toBlob((blob) => {
                if (!blob) {
                    this.capturing.set(false);
                    this.uploadError.set('examination.recapture.teacher.error');
                    this.cdr.markForCheck();
                    return;
                }
                const file = new File([blob], `recapture_${Date.now()}.jpg`, { type: 'image/jpeg' });
                this.capturedFile.set(file);
                this.viewMode.set('review');
                this.capturing.set(false);
                // Réinitialiser l'état d'édition pour la nouvelle capture.
                this.transform = { translateUnit: 'px', scale: 1 };
                this.canvasRotation = 0;
                this.zoom.set(1);
                this.croppedPreviewUrl.set(null);
                this.lastCroppedBlob = null;
                this.cdr.markForCheck();
                // Diffuser une version légère aux autres participants (élève).
                this.broadcastCapturedFrame(canvas);
            }, 'image/jpeg', 0.92);
        } catch (err) {
            // Canvas potentiellement tainté (cross-origin) — improbable avec ACS, mais sécurisé.
            this.capturing.set(false);
            this.uploadError.set('examination.recapture.teacher.tainted');
        }
    }

    retake(): void {
        this.capturedFile.set(null);
        this.croppedPreviewUrl.set(null);
        this.lastCroppedBlob = null;
        this.zoom.set(1);
        this.transform = { translateUnit: 'px', scale: 1 };
        this.canvasRotation = 0;
        this.uploadError.set(null);
        this.viewMode.set('live');
        // Prévenir l'élève que la photo figée n'est plus valable : il revoit sa cam.
        this.sendWs('photo_retake', {});
        // Réamorcer la lecture du flux cloné si elle a été mise en pause.
        const liveVideo = this.liveVideoRef?.nativeElement;
        if (liveVideo && liveVideo.paused) {
            liveVideo.play().catch(() => { /* noop */ });
        }
        // Le bloc live est recréé par le *ngIf, il faut ré-attacher le stream sur
        // le nouvel élément <video> après le rendu Angular.
        setTimeout(() => this.attachRemoteStream(), 0);
    }

    /** Callback du cropper : on stocke le blob et l'aperçu rond. */
    onCropped(event: ImageCroppedEvent): void {
        this.lastCroppedBlob = event.blob ?? null;
        if (event.objectUrl) {
            this.croppedPreviewUrl.set(this.sanitizer.bypassSecurityTrustUrl(event.objectUrl));
        }
        this.cdr.markForCheck();
    }

    onZoomChange(value: number): void {
        const clamped = Math.max(1, Math.min(3, value));
        this.zoom.set(clamped);
        this.transform = { ...this.transform, scale: clamped };
    }

    rotateLeft(): void {
        this.canvasRotation = (this.canvasRotation + 3) % 4;
        this.flipAfterRotate();
    }

    rotateRight(): void {
        this.canvasRotation = (this.canvasRotation + 1) % 4;
        this.flipAfterRotate();
    }

    private flipAfterRotate(): void {
        // Conserve la cohérence visuelle après rotation 90° (cf. implémentation
        // de référence dans ImageCropperPreviewComponent).
        this.transform = {
            ...this.transform,
            flipH: this.transform.flipV,
            flipV: this.transform.flipH,
        };
    }

    async validate(): Promise<void> {
        if (!this.lastCroppedBlob) {
            return;
        }
        this.viewMode.set('uploading');
        this.uploadError.set(null);
        const filename = `photo_identity_${Date.now()}.jpg`;
        const file = new File([this.lastCroppedBlob], filename, { type: 'image/jpeg' });
        this.profilePhotosService.postProfilePhoto(this.data.user, file, 'validated').subscribe({
            next: (profilePhoto) => {
                // Diffuser au reste de la room d'examen.
                this.sendWs('photo_validated', {
                    profile_photo_id: profilePhoto?.id,
                    photo_url: (profilePhoto as any)?.photo_identity || null,
                });
                this.dialogRef.close({
                    status: 'validated',
                    profilePhotoId: profilePhoto?.id,
                    photoUrl: (profilePhoto as any)?.photo_identity || undefined,
                });
            },
            error: () => {
                this.uploadError.set('examination.recapture.teacher.error');
                this.viewMode.set('review');
                this.cdr.markForCheck();
            },
        });
    }

    cancel(): void {
        if (this.data.mode === 'teacher') {
            this.sendWs('cancel_photo_recapture', {});
        }
        this.dialogRef.close({ status: 'cancelled' });
    }

    /**
     * Méthode publique utilisée par le parent (ExaminationRoomComponent côté élève)
     * pour fermer le modal lorsqu'un message `cancel_photo_recapture` ou
     * `photo_validated` est reçu.
     */
    public closeFromRemote(status: 'validated' | 'cancelled' = 'cancelled'): void {
        this.dialogRef.close({ status });
    }

    /**
     * Élève : la formatrice vient de capturer la photo (event WS `photo_captured`).
     * On fige l'aperçu sur cette image au lieu de la cam live.
     */
    public showCapturedFromTeacher(dataUrl: string): void {
        if (this.data.mode !== 'student' || !dataUrl) {
            return;
        }
        this.frozenPhotoUrl.set(dataUrl);
        this.cdr.markForCheck();
    }

    /**
     * Élève : la formatrice a cliqué "Reprendre" (event WS `photo_retake`).
     * On revient au flux cam local.
     */
    public clearCapturedFromTeacher(): void {
        if (this.data.mode !== 'student') {
            return;
        }
        this.frozenPhotoUrl.set(null);
        this.finalPhotoUrl.set(null);        this.cdr.markForCheck();        // Le *ngIf recr\u00e9e <video #liveVideo> : on doit r\u00e9attacher le srcObject.
        setTimeout(() => this.attachLocalStream(), 0);
    }

    /**
     * Élève : photo validée (event WS `photo_validated`). On affiche l'image finale
     * (servie par l'API) ; la fermeture du modal est pilotée par closeFromRemote().
     */
    public showValidatedFromTeacher(photoUrl: string | null): void {
        if (this.data.mode !== 'student' || !photoUrl) {
            return;
        }
        this.finalPhotoUrl.set(photoUrl);
        this.cdr.markForCheck();
    }

    /**
     * Diffuse aux autres participants (en pratique : l'élève) une version
     * downscaleée de la capture pour limiter la taille du payload WebSocket.
     */
    private broadcastCapturedFrame(sourceCanvas: HTMLCanvasElement): void {
        try {
            const targetSize = 320;
            const canvas = document.createElement('canvas');
            canvas.width = targetSize;
            canvas.height = targetSize;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const src = Math.min(sourceCanvas.width, sourceCanvas.height);
            const sx = (sourceCanvas.width - src) / 2;
            const sy = (sourceCanvas.height - src) / 2;
            ctx.drawImage(sourceCanvas, sx, sy, src, src, 0, 0, targetSize, targetSize);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            this.sendWs('photo_captured', { data_url: dataUrl });
        } catch (_) {
            // Pas bloquant : la fonctionnalité principale (capture/upload) continue.
        }
    }

    private sendWs(actionType: string, payload: any): void {
        if (!this.data.me || !this.data.examinationSessionId) {
            return;
        }
        const params = {
            message_type: 'examination_session',
            object_id: this.data.examinationSessionId,
            action_type: actionType,
            data: payload,
        };
        const message = this.websocketService.prepare_message_data(this.data.me, params);
        this.websocketService.send(message);
    }
}
